import * as React from 'react'

import { format } from 'date-fns'
import { Calendar as CalendarIcon } from 'lucide-react'

import { cn } from '../../lib/utils'
import { Button } from '../../lib/ui/button'
import { Calendar } from '../../lib/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '../../lib/ui/popover'

import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../lib/ui/form'

export function DatePickerField({
  control,
  name,
  label,
  description,
  programSurvey = false,
}) {
  const fieldId = React.useId()
  const triggerRef = React.useRef(null)
  const [open, setOpen] = React.useState(false)

  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        const helpId = `${fieldId}-help`
        const descriptionId = `${fieldId}-description`
        const errorId = `${fieldId}-error`
        const describedBy = fieldState.error
          ? `${helpId} ${descriptionId} ${errorId}`
          : `${helpId} ${descriptionId}`

        return (
          <FormItem className="flex flex-col">
            <FormLabel id={`${fieldId}-label`}>{label}</FormLabel>
            <FormDescription
              id={helpId}
              className="sr-only mt-4 text-sm text-[#5d5d5d]"
            >
              Choose a date from the calendar popover
            </FormDescription>
            <Popover open={open} onOpenChange={setOpen}>
              <FormControl>
                <PopoverTrigger asChild>
                  <Button
                    ref={triggerRef}
                    variant={'outline'}
                    type="button"
                    aria-labelledby={`${fieldId}-label`}
                    aria-describedby={describedBy}
                    aria-invalid={fieldState.invalid}
                    aria-haspopup="dialog"
                    className={cn(
                      'w-[240px] pl-3 text-left font-normal',
                      programSurvey && '!mt-12',
                      !field.value && 'text-muted-foreground',
                    )}
                  >
                    {field.value ? (
                      format(field.value, 'PPP')
                    ) : (
                      <span>Pick a date</span>
                    )}
                    <CalendarIcon
                      className="ml-auto h-4 w-4 opacity-50"
                      aria-hidden="true"
                    />
                  </Button>
                </PopoverTrigger>
              </FormControl>
              <PopoverContent
                className="w-auto p-0"
                align="start"
                role="dialog"
                aria-label={label}
                onCloseAutoFocus={(event) => {
                  event.preventDefault()
                  triggerRef.current?.focus()
                }}
              >
                <Calendar
                  mode="single"
                  selected={field.value}
                  onSelect={(date) => {
                    field.onChange(date)
                    setOpen(false)
                  }}
                  disabled={(date) => date < new Date('1900-01-01')}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
            <FormDescription id={descriptionId}>{description}</FormDescription>
            <FormMessage id={errorId} aria-live="polite" />
          </FormItem>
        )
      }}
    />
  )
}
